import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { userApi } from './api';

// Synchronous action creator for clearing selected user
export const clearSelectedUser = createAction('users/clearSelectedUser');

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await userApi.createUser(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create user');
    }
  }
);

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await userApi.getUsers(page, limit);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch users');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId, { rejectWithValue, getState, dispatch }) => {
    try {
      await userApi.deleteUser(userId);
      // Get current pagination state
      const { page, limit } = getState().users;
      // Refetch users list
      await dispatch(fetchUsers({ page, limit }));
      return userId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete user');
    }
  }
);

export const fetchUserDetails = createAsyncThunk(
  'users/fetchUserDetails',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userApi.getUserDetails(userId);
      return response.data.user;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch user details');
    }
  }
);

export const toggleUserStatus = createAsyncThunk(
  'users/toggleUserStatus',
  async ({ userId, activate }, { rejectWithValue, dispatch }) => {
    try {
      const response = await (activate ? 
        userApi.activateUser(userId) : 
        userApi.deactivateUser(userId));
      
      // Refresh user details after status change
      await dispatch(fetchUserDetails(userId));
      await dispatch(fetchUsers({ page: 1 })); // Refresh users list
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update user status');
    }
  }
);

export const verifyUserDocuments = createAsyncThunk(
  'users/verifyDocuments',
  async ({ userId, verificationData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await userApi.verifyDocuments(userId, verificationData);
      
      // Refresh user details after verification
      await dispatch(fetchUserDetails(userId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to verify documents');
    }
  }
);

export const fetchAllClients = createAsyncThunk(
  'users/fetchAllClients',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userApi.getAllClients(1, 0); // limit=0 to get all clients
      return response.data.users; // Assuming the response contains a users array
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch clients');
    }
  }
);
